import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

export const SHORT_FORM_CONTENT_TYPE = {
  // ------------ Social ------------
  facebookPost: 'facebook_post',
  instagramPost: 'instagram_caption',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  youtubeVideoTitle: 'youtube_video_title',
  youtubeVideoDescription: 'youtube_video_description',
  // ------------ Ads ------------
  facebookAd: 'facebook_ad',
  facebookAdPrimaryText: 'facebook_ad_primary_text',
  facebookAdHeadline: 'facebook_ad_headline',
  linkedinAd: 'linkedin_ad',
  twitterAd: 'twitter_ad',
  twitterAdCopy: 'twitter_ad_copy',
  twitterAdHeadline: 'twitter_ad_headline',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  googleAdHeadline: 'google_ad_headline',
  googleDemandGenAdHeadline: 'google_demand_gen_ad_headline',
  googleAdLongHeadline: 'google_ad_long_headline',
  googleAdDescription: 'google_ad_description',
  pinterestAd: 'pinterest_ad',
  pinterestAdTitle: 'pinterest_ad_title',
  pinterestAdDescription: 'pinterest_ad_description',
  outbrainAd: 'outbrain_ad',
  outbrainAdTitle: 'outbrain_ad_title',
  outbrainAdDescription: 'outbrain_ad_description',
  taboolaAd: 'taboola_ad',
  taboolaAdTitle: 'taboola_ad_title',
  taboolaAdDescription: 'taboola_ad_description',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',
  linkedinAdIntroText: 'linkedin_ad_intro_text',
  linkedinAdHeadline: 'linkedin_ad_headline',
  // Email
  email: 'email', // TODO: check if need to remove
  emailSubjectLine: 'email_subject_line',
  emailHeadline: 'email_headline',
  emailSectionHeadline: 'email_section_headline',
  emailIntroduction: 'email_introduction',
  emailCallToAction: 'email_call_to_action',
  // ------------ Website ------------
  // Blog
  blogHeadline: 'blog_headline',
  blogIntroParagraph: 'blog_intro_paragraph',
  blogSectionHeadline: 'blog_section_headline',
  blogSectionSubHeadline: 'blog_section_sub_headline',
  // Webpage
  webpageHeadline: 'webpage_headline',
  webpageSubHeadline: 'webpage_sub_headline',
  webpageSectionHeadline: 'webpage_section_headline',
  webpageCallToAction: 'webpage_call_to_action',
  webpageBenefits: 'webpage_benefits',
  webpageFeatures: 'webpage_features',

  seoMetaDescription: 'seo_meta_description',
  // ------------ Ecommerce ------------
  amazonProductListing: 'amazon_product_listing',
  walmartProductListing: 'walmart_product_listing',
  generalProductListing: 'general_product_listing',
  sms: 'sms',
  // ------------ Images ------------
  generalImage: 'general_image',
  // ------------ Other ------------
  generalText: 'general_text',
} as const;
export type ShortFormContentType =
  (typeof SHORT_FORM_CONTENT_TYPE)[keyof typeof SHORT_FORM_CONTENT_TYPE];

export const shortFormContentTypeHasScreenshotInput = (
  contentType: ShortFormContentType
): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.facebookPost,
    SHORT_FORM_CONTENT_TYPE.instagramPost,
    SHORT_FORM_CONTENT_TYPE.linkedinPost,
    SHORT_FORM_CONTENT_TYPE.twitterPost,
    SHORT_FORM_CONTENT_TYPE.facebookAd,
    SHORT_FORM_CONTENT_TYPE.linkedinAd,
    SHORT_FORM_CONTENT_TYPE.twitterAd,
    SHORT_FORM_CONTENT_TYPE.pinterestAd,
    SHORT_FORM_CONTENT_TYPE.outbrainAd,
    SHORT_FORM_CONTENT_TYPE.taboolaAd,
    SHORT_FORM_CONTENT_TYPE.linkedinTextAd,
  ];
  return types.includes(contentType);
};

export const shortFormContentTypeHasCSVInput = (contentType: ShortFormContentType): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

export const shortFormContentTypeHasSectionNumbering = (
  contentType: ShortFormContentType
): boolean => {
  const types: ShortFormContentType[] = [
    SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd,
    SHORT_FORM_CONTENT_TYPE.googleSearchAd,
  ];
  return types.includes(contentType);
};

interface ShortFormContentTypeProps {
  title: string;
  generationTool: GenerationTool;
  withSingleAsset: boolean;
}

const SHORT_FORM_CONTENT_TYPE_PROPS: Record<ShortFormContentType, ShortFormContentTypeProps> = {
  // ------------ Social ------------
  [SHORT_FORM_CONTENT_TYPE.facebookPost]: {
    title: 'Facebook post',
    generationTool: GENERATION_TOOL.facebookPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.instagramPost]: {
    title: 'Instagram post',
    generationTool: GENERATION_TOOL.instagramCaption,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinPost]: {
    title: 'LinkedIn post',
    generationTool: GENERATION_TOOL.linkedinPost,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterPost]: {
    title: 'X post',
    generationTool: GENERATION_TOOL.tweet,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.youtubeVideoTitle]: {
    title: 'Youtube Video - Title',
    generationTool: GENERATION_TOOL.youtubeTitle,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.youtubeVideoDescription]: {
    title: 'Youtube Video - Description',
    generationTool: GENERATION_TOOL.youtubeDescription,
    withSingleAsset: true,
  },
  // ------------ Ads ------------
  [SHORT_FORM_CONTENT_TYPE.facebookAd]: {
    title: 'Meta Image ad',
    generationTool: GENERATION_TOOL.facebookMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.facebookAdPrimaryText]: {
    title: 'Meta Ad - Primary text',
    generationTool: GENERATION_TOOL.facebookPrimaryTextProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.facebookAdHeadline]: {
    title: 'Meta Ad - Headline',
    generationTool: GENERATION_TOOL.facebookHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinAd]: {
    title: 'LinkedIn Single Image ad',
    generationTool: GENERATION_TOOL.linkedinMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinConversationAd]: {
    title: 'LinkedIn Conversation ad',
    generationTool: GENERATION_TOOL.linkedinConversationAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinMessageAd]: {
    title: 'LinkedIn Message ad',
    generationTool: GENERATION_TOOL.linkedinMessageAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinLeadGenFormAd]: {
    title: 'LinkedIn Lead Gen Form ad',
    generationTool: GENERATION_TOOL.linkedinLeadGenFormAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinDocumentAd]: {
    title: 'LinkedIn Document ad',
    generationTool: GENERATION_TOOL.linkedinDocumentAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinTextAd]: {
    title: 'LinkedIn Text ad',
    generationTool: GENERATION_TOOL.linkedinTextAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinVideoAd]: {
    title: 'LinkedIn Video ad',
    generationTool: GENERATION_TOOL.linkedinVideoAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinAdIntroText]: {
    title: 'LinkedIn Ad - Introductory text',
    generationTool: GENERATION_TOOL.linkedinIntroSingleImage,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.linkedinAdHeadline]: {
    title: 'LinkedIn Ad - Headline',
    generationTool: GENERATION_TOOL.linkedinHeadlineSingleImage,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.googlePerformanceMaxAd]: {
    title: 'Google Performance Max ad',
    generationTool: GENERATION_TOOL.googlePerformanceMaxAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googleSearchAd]: {
    title: 'Google Search ad',
    generationTool: GENERATION_TOOL.googleRSA,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.googleAdHeadline]: {
    title: 'Google Ad - Headline',
    generationTool: GENERATION_TOOL.googleHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.googleDemandGenAdHeadline]: {
    title: 'Google Demand Gen Ad - Headline',
    generationTool: GENERATION_TOOL.googleMediumHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.googleAdLongHeadline]: {
    title: 'Google Ad - Long headline',
    generationTool: GENERATION_TOOL.googleLongHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.googleAdDescription]: {
    title: 'Google Ad - Description',
    generationTool: GENERATION_TOOL.googleDescriptionProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterAd]: {
    title: 'X ad',
    generationTool: GENERATION_TOOL.twitterMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterAdCopy]: {
    title: 'X Ad - Copy',
    generationTool: GENERATION_TOOL.twitterCopyProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.twitterAdHeadline]: {
    title: 'X Ad - Headline',
    generationTool: GENERATION_TOOL.twitterTitleProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.pinterestAd]: {
    title: 'Pinterest ad',
    generationTool: GENERATION_TOOL.pinterestMixtureAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.pinterestAdTitle]: {
    title: 'Pinterest Ad - Title',
    generationTool: GENERATION_TOOL.pinterestTitleProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.pinterestAdDescription]: {
    title: 'Pinterest Ad - Description',
    generationTool: GENERATION_TOOL.pinterestDescriptionProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.outbrainAd]: {
    title: 'Outbrain ad',
    generationTool: GENERATION_TOOL.outbrainAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.outbrainAdTitle]: {
    title: 'Outbrain Ad - Title',
    generationTool: GENERATION_TOOL.outbrainHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.outbrainAdDescription]: {
    title: 'Outbrain Ad - Description',
    generationTool: GENERATION_TOOL.outbrainDescriptionProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.taboolaAd]: {
    title: 'Taboola ad',
    generationTool: GENERATION_TOOL.taboolaAd,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.taboolaAdTitle]: {
    title: 'Taboola Ad - Title',
    generationTool: GENERATION_TOOL.taboolaHeadlineProduct,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.taboolaAdDescription]: {
    title: 'Taboola Ad - Description',
    generationTool: GENERATION_TOOL.taboolaDescriptionProduct,
    withSingleAsset: true,
  },
  // ------------ Email ------------
  [SHORT_FORM_CONTENT_TYPE.email]: {
    title: 'Email',
    generationTool: GENERATION_TOOL.emailMixture,
    withSingleAsset: false,
  },
  [SHORT_FORM_CONTENT_TYPE.emailSubjectLine]: {
    title: 'Email - Subject line',
    generationTool: GENERATION_TOOL.emailSubjectLine,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.emailHeadline]: {
    title: 'Email - Headline',
    generationTool: GENERATION_TOOL.emailHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.emailSectionHeadline]: {
    title: 'Email - Section headline',
    generationTool: GENERATION_TOOL.emailSectionHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.emailIntroduction]: {
    title: 'Email - Introduction',
    generationTool: GENERATION_TOOL.emailIntroParagraph,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.emailCallToAction]: {
    title: 'Email - Call to action',
    generationTool: GENERATION_TOOL.emailCta,
    withSingleAsset: true,
  },

  // ------------ Images ------------
  [SHORT_FORM_CONTENT_TYPE.generalImage]: {
    title: 'General image',
    generationTool: GENERATION_TOOL.imageImprove,
    withSingleAsset: false,
  },
  // ------------ Website ------------
  // Blog
  [SHORT_FORM_CONTENT_TYPE.blogHeadline]: {
    title: 'Blog - Headline',
    generationTool: GENERATION_TOOL.articleBlogHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.blogIntroParagraph]: {
    title: 'Blog - Intro paragraph',
    generationTool: GENERATION_TOOL.blogPostIntroParagraph,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.blogSectionHeadline]: {
    title: 'Blog - Section headline',
    generationTool: GENERATION_TOOL.blogPostSectionHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.blogSectionSubHeadline]: {
    title: 'Blog - Section subheadline',
    generationTool: GENERATION_TOOL.blogPostSectionSubHeadline,
    withSingleAsset: true,
  },
  // Webpage
  [SHORT_FORM_CONTENT_TYPE.webpageHeadline]: {
    title: 'Webpage - Headline',
    generationTool: GENERATION_TOOL.landingHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageSubHeadline]: {
    title: 'Webpage - Subheadline',
    generationTool: GENERATION_TOOL.landingSubheader,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageSectionHeadline]: {
    title: 'Webpage - Section headline',
    generationTool: GENERATION_TOOL.landingSectionHeadline,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageCallToAction]: {
    title: 'Webpage - Call to action',
    generationTool: GENERATION_TOOL.landingCTA,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageBenefits]: {
    title: 'Webpage - Benefits',
    generationTool: GENERATION_TOOL.landingBenefits,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.webpageFeatures]: {
    title: 'Webpage - Features',
    generationTool: GENERATION_TOOL.landingFeatures,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.seoMetaDescription]: {
    title: 'SEO meta description',
    generationTool: GENERATION_TOOL.landingSEOMetadata,
    withSingleAsset: false,
  },

  // ------------ Ecommerce ------------
  [SHORT_FORM_CONTENT_TYPE.amazonProductListing]: {
    title: 'Amazon product listing',
    generationTool: GENERATION_TOOL.amazonProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.walmartProductListing]: {
    title: 'Walmart product listing',
    generationTool: GENERATION_TOOL.walmartProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.generalProductListing]: {
    title: 'Product Review',
    generationTool: GENERATION_TOOL.generalProductListing,
    withSingleAsset: true,
  },
  [SHORT_FORM_CONTENT_TYPE.sms]: {
    title: 'SMS',
    generationTool: GENERATION_TOOL.smsPromotional,
    withSingleAsset: true,
  },
  // ------------ Other ------------
  [SHORT_FORM_CONTENT_TYPE.generalText]: {
    title: 'Other',
    generationTool: GENERATION_TOOL.basicContentImprover,
    withSingleAsset: false,
  },
};

export const getShortFormContentTypeTitle = (contentType: ShortFormContentType): string =>
  SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.title || contentType;

export const getShortFormContentTypeTool = (contentType: ShortFormContentType): GenerationTool => {
  return SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.generationTool;
};

export const isSingleAssetRewriteContentType = (contentType: ShortFormContentType): boolean => {
  return contentType ? Boolean(SHORT_FORM_CONTENT_TYPE_PROPS[contentType]?.withSingleAsset) : false;
};
